import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHeader from './Navigation/PageHeader';
import { motion } from 'framer-motion';
import * as staffService from '../services/staffService';
import * as caseService from '../services/caseService';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { checkStaffDependencies } from '../services/dependencyService';
import StaffTimeline from './StaffTimeline';
import useModalAccessibility from '../hooks/useModalAccessibility';
import { useAuth } from './Auth/AuthProvider';
import usePersistedTab from '../hooks/usePersistedTab';

const {
  FiUsers,
  FiUser,
  FiSearch,
  FiFilter,
  FiEye,
  FiAlertTriangle,
  FiCheck,
  FiClock,
  FiFileText,
  FiCalendar,
  FiPhone,
  FiMail,
  FiMapPin,
  FiTrendingUp,
  FiTrendingDown,
  FiPlus,
  FiEdit3,
  FiDownload,
  FiUpload,
  FiBarChart3,
  FiActivity,
  FiShield,
  FiTarget,
  FiAward,
  FiX,
  FiTrash2
} = FiIcons;

const StaffManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [staff, setStaff] = useState([]);
  const [cases, setCases] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = usePersistedTab('grid', 'view');
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [showEditStaffForm, setShowEditStaffForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [staffToEdit, setStaffToEdit] = useState(null);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [staffDependencies, setStaffDependencies] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();

    if (location.state?.filterEmployee) {
      setSelectedEmployee(location.state.filterEmployee);
    }

    if (location.state?.action === 'create') {
      setShowStaffForm(true);
    }
  }, [location.state]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load both staff and cases from Supabase
      const [staffData, casesData] = await Promise.all([
        staffService.getAllStaff(),
        caseService.getAllCases()
      ]);

      setStaff(staffData);
      setCases(casesData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStaffCases = (staffId) => {
    return cases.filter(case_ => case_.staff_id === staffId);
  };

  const getStaffStats = (staffId) => {
    const staffCases = getStaffCases(staffId);
    return {
      totalCases: staffCases.length,
      activeCases: staffCases.filter(c => c.status === 'active').length,
      completedCases: staffCases.filter(c => c.status === 'completed').length,
      highPriorityCases: staffCases.filter(c => c.priority === 'high').length
    };
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getEmploymentStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'on-leave': return 'text-yellow-600 bg-yellow-50';
      case 'suspended': return 'text-red-600 bg-red-50';
      case 'terminated': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPerformanceColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const filteredStaff = staff.filter(person => {
    const matchesSearch =
      person.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.employee_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.position.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartment = filterDepartment === 'all' || person.department === filterDepartment;
    const matchesStatus = filterStatus === 'all' || person.employment_status === filterStatus;

    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const departments = [...new Set(staff.map(person => person.department))];

  // Stats for page header
  const headerStats = [
    {
      label: 'Total Staff',
      value: staff.length,
      icon: FiUsers,
      iconColor: 'text-blue-600'
    },
    {
      label: 'Active Cases',
      value: cases.filter(c => c.status === 'active').length,
      icon: FiActivity,
      iconColor: 'text-orange-600'
    },
    {
      label: 'High Risk',
      value: staff.filter(s => s.risk_level === 'high').length,
      icon: FiAlertTriangle,
      iconColor: 'text-red-600'
    },
    {
      label: 'Avg Performance',
      value:
        staff.length > 0
          ? (staff.reduce((sum, s) => sum + (s.performance_rating || 0), 0) / staff.length).toFixed(1)
          : '0.0',
      icon: FiTrendingUp,
      iconColor: 'text-green-600'
    }
  ];

  // Only admins can add staff
  const headerActions = isAdmin ? [
    {
      type: 'button',
      label: 'Add Staff',
      icon: FiPlus,
      variant: 'primary',
      onClick: () => setShowStaffForm(true)
    }
  ] : [];

  if (loading) {
    return (
      <>
        <PageHeader
          title="Staff Management"
          subtitle="Comprehensive staff overview with case tracking and risk management"
          stats={headerStats}
          actions={headerActions}
        />
        <div className="max-w-7xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Loading staff data...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader
          title="Staff Management"
          subtitle="Comprehensive staff overview with case tracking and risk management"
          stats={headerStats}
          actions={headerActions}
        />
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-4xl mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Data</h3>
            <p className="text-red-700 mb-4">{error}</p>
            <button onClick={loadData} className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700">
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Staff Management"
        subtitle="Comprehensive staff overview with case tracking and risk management"
        stats={headerStats}
        actions={headerActions}
      />

      <div className="max-w-7xl mx-auto p-6">
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <SafeIcon
                  icon={FiSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search staff by name, ID, or position..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <select
              value={filterDepartment}
              onChange={e => setFilterDepartment(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="on-leave">On Leave</option>
              <option value="suspended">Suspended</option>
              <option value="terminated">Terminated</option>
            </select>

            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
                  } rounded-l-lg`}
                aria-label="Switch to Grid View"
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`hidden md:block px-3 py-2 ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
                  } rounded-r-lg border-l`}
                aria-label="Switch to List View"
              >
                List
              </button>
            </div>
          </div>
        </div>

        {/* Staff Display - Grid View */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStaff.map((person, index) => {
              const stats = getStaffStats(person.id);

              return (
                <motion.div
                  key={person.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <div className="p-6">
                    {/* Header with photo and basic info */}
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {person.full_name
                          .split(' ')
                          .map(n => n[0])
                          .join('')}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900 mb-1">{person.full_name}</h3>
                        <p className="text-sm text-gray-600">{person.position}</p>
                        <p className="text-xs text-gray-500">
                          {person.employee_id} • {person.department}
                        </p>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getEmploymentStatusColor(
                            person.employment_status
                          )}`}
                        >
                          {person.employment_status.replace('-', ' ').toUpperCase()}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium border ${getRiskLevelColor(
                            person.risk_level
                          )}`}
                        >
                          {person.risk_level.toUpperCase()} RISK
                        </span>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <SafeIcon icon={FiMail} className="text-gray-400" />
                        <span className="truncate">{person.email}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <SafeIcon icon={FiPhone} className="text-gray-400" />
                        <span>{person.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <SafeIcon icon={FiMapPin} className="text-gray-400" />
                        <span>{person.location}</span>
                      </div>
                    </div>

                    {/* Performance Rating */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600">Performance Rating</span>
                        <span className={`font-medium ${getPerformanceColor(person.performance_rating)}`}>
                          {person.performance_rating}/5.0
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(person.performance_rating / 5) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Case Statistics */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="text-center p-2 bg-blue-50 rounded">
                        <p className="text-sm font-medium text-blue-600">{stats.totalCases}</p>
                        <p className="text-xs text-blue-500">Total Cases</p>
                      </div>
                      <div className="text-center p-2 bg-orange-50 rounded">
                        <p className="text-sm font-medium text-orange-600">{stats.activeCases}</p>
                        <p className="text-xs text-orange-500">Active Cases</p>
                      </div>
                    </div>

                    {/* Key Dates */}
                    <div className="space-y-1 mb-4 text-xs text-gray-500">
                      <div className="flex justify-between">
                        <span>Start Date:</span>
                        <span>{new Date(person.start_date).toLocaleDateString()}</span>
                      </div>
                      {person.last_review_date && (
                        <div className="flex justify-between">
                          <span>Last Review:</span>
                          <span>{new Date(person.last_review_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Line Manager:</span>
                        <span>{person.line_manager}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/staff/${person.id}`)}
                        className="flex-1 bg-blue-600 text-white text-center py-2 rounded hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        View Profile
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => {
                              setStaffToEdit(person);
                              setShowEditStaffForm(true);
                            }}
                            className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            title="Edit Staff"
                            aria-label={`Edit staff ${person.full_name}`}
                          >
                            <SafeIcon icon={FiEdit3} className="text-gray-600" />
                          </button>
                          <button
                            onClick={async () => {
                              setStaffToDelete(person);
                              const dependencies = await checkStaffDependencies(person.id);
                              setStaffDependencies(dependencies);
                              setShowDeleteConfirm(true);
                            }}
                            className="px-3 py-2 border border-red-300 rounded hover:bg-red-50 transition-colors"
                            title="Delete Staff"
                            aria-label={`Delete staff ${person.full_name}`}
                          >
                            <SafeIcon icon={FiTrash2} className="text-red-600" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Staff Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cases
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Risk Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStaff.map(person => {
                    const stats = getStaffStats(person.id);

                    return (
                      <tr key={person.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {person.full_name
                                .split(' ')
                                .map(n => n[0])
                                .join('')}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{person.full_name}</div>
                              <div className="text-sm text-gray-500">{person.position}</div>
                              <div className="text-xs text-gray-400">{person.employee_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{person.department}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEmploymentStatusColor(
                              person.employment_status
                            )}`}
                          >
                            {person.employment_status.replace('-', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex space-x-4">
                            <span className="text-blue-600 font-medium">{stats.totalCases} total</span>
                            <span className="text-orange-600">{stats.activeCases} active</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`text-sm font-medium mr-2 ${getPerformanceColor(person.performance_rating)}`}>
                              {person.performance_rating}
                            </span>
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${(person.performance_rating / 5) * 100}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRiskLevelColor(
                              person.risk_level
                            )}`}
                          >
                            {person.risk_level.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2 justify-end">
                            <button
                              onClick={() => navigate(`/staff/${person.id}`)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Profile"
                              aria-label={`View profile for ${person.full_name}`}
                            >
                              <SafeIcon icon={FiEye} className="w-5 h-5" />
                            </button>
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => {
                                    setStaffToEdit(person);
                                    setShowEditStaffForm(true);
                                  }}
                                  className="text-gray-600 hover:text-gray-900"
                                  title="Edit Staff"
                                  aria-label={`Edit staff ${person.full_name}`}
                                >
                                  <SafeIcon icon={FiEdit3} />
                                </button>
                                <button
                                  onClick={async () => {
                                    setStaffToDelete(person);
                                    const dependencies = await checkStaffDependencies(person.id);
                                    setStaffDependencies(dependencies);
                                    setShowDeleteConfirm(true);
                                  }}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Staff"
                                  aria-label={`Delete staff ${person.full_name}`}
                                >
                                  <SafeIcon icon={FiTrash2} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filteredStaff.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiUsers} className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No staff found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'No staff match your search criteria.' : 'No staff members available.'}
            </p>
          </div>
        )}

        {/* New Staff Form Modal */}
        {showStaffForm && (
          <NewStaffModal
            onSubmit={async (staffData) => {
              console.log('🎯 onSubmit handler called with:', staffData);
              try {
                console.log('📞 Calling staffService.createStaff...');
                const result = await staffService.createStaff(staffData);
                console.log('✅ Staff created:', result);
                console.log('✅ Reloading data...');
                await loadData();
                setShowStaffForm(false);
                console.log('✅ Modal closed');
              } catch (err) {
                console.error('❌ Error creating staff:', err);
                console.error('❌ Error details:', {
                  message: err.message,
                  name: err.name,
                  stack: err.stack
                });
                // Re-throw to let modal handle the error display
                throw err;
              }
            }}
            onCancel={() => setShowStaffForm(false)}
          />
        )}

        {/* Edit Staff Form Modal */}
        {showEditStaffForm && staffToEdit && (
          <EditStaffModal
            staffData={staffToEdit}
            onSubmit={async (updates) => {
              try {
                setLoading(true);
                await staffService.updateStaff(staffToEdit.id, updates);
                await loadData();
                setShowEditStaffForm(false);
                setStaffToEdit(null);
              } catch (err) {
                console.error('Error updating staff:', err);
                setError('Failed to update staff. Please try again.');
                throw err;
              } finally {
                setLoading(false);
              }
            }}
            onCancel={() => {
              setShowEditStaffForm(false);
              setStaffToEdit(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteConfirm && staffToDelete !== null}
          onClose={() => {
            setShowDeleteConfirm(false);
            setStaffToDelete(null);
            setStaffDependencies({});
          }}
          onConfirm={async () => {
            try {
              setDeleteLoading(true);
              await staffService.deleteStaff(staffToDelete.id);
              await loadData();
              setShowDeleteConfirm(false);
              setStaffToDelete(null);
              setStaffDependencies({});
            } catch (err) {
              console.error('Error deleting staff:', err);
              setError('Failed to delete staff. Please try again.');
            } finally {
              setDeleteLoading(false);
            }
          }}
          itemType="Staff Member"
          itemName={staffToDelete?.full_name || ''}
          dependencies={staffDependencies}
          loading={deleteLoading}
          blockingDependencies={true}
        />

        {/* Staff Detail Modal - DISABLED: Now using dedicated page route */}
        {/* {selectedStaff && (
          <StaffDetailModal
            staff={selectedStaff}
            cases={getStaffCases(selectedStaff.id)}
            onClose={() => setSelectedStaff(null)}
          />
        )} */}
      </div>
    </>
  );
};

// New Staff Form Modal Component
const NewStaffModal = ({ onSubmit, onCancel }) => {
  const modalRef = useModalAccessibility(true, onCancel);
  // Generate auto-filled employee ID: EMP#### (4 random digits)
  const generateEmployeeId = () => {
    const randomNum = Math.floor(1000 + Math.random() * 9000); // 4-digit number
    return `EMP${randomNum}`;
  };

  const [formData, setFormData] = useState({
    employee_id: generateEmployeeId(), // Auto-filled, user can change
    full_name: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    line_manager: '',
    location: '',
    employment_status: 'active',
    start_date: new Date().toISOString().split('T')[0],
    salary: '100000', // Default sample salary, user can change
    risk_level: 'low',
    performance_rating: 0,
    last_review_date: '' // Not shown in UI, will be converted to null by backend
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.employee_id || !formData.full_name || !formData.email) {
      setError('Please fill in all required fields: Employee ID, Full Name, and Email');
      return;
    }

    if (!formData.department) {
      setError('Please select a department');
      return;
    }

    if (!formData.position) {
      setError('Please enter a position');
      return;
    }

    if (!formData.start_date) {
      setError('Please select a start date');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
      // If we get here, submission was successful
      // Parent will close the modal
    } catch (error) {
      console.error('❌ Error in handleSubmit:', error);
      // Extract user-friendly error message
      let errorMessage = 'Failed to create staff member. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      setError(errorMessage);
      // Don't close modal on error - let user retry
    } finally {
      // Always reset submitting state to stop spinner
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Staff Member</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            <p className="text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.employee_id}
                onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., EMP001"
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="John Doe"
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="john@example.com"
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="+1 (555) 123-4567"
                disabled={submitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                disabled={submitting}
              >
                <option value="">Select Department</option>
                <option value="HR">Human Resources</option>
                <option value="Finance">Finance</option>
                <option value="IT">Information Technology</option>
                <option value="Operations">Operations</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
                <option value="Customer Service">Customer Service</option>
                <option value="Care Services">Care Services</option>
                <option value="Engineering">Engineering</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Senior Manager"
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Line Manager</label>
              <input
                type="text"
                value={formData.line_manager}
                onChange={(e) => setFormData(prev => ({ ...prev, line_manager: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Manager name"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., London Office"
                disabled={submitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
              <select
                value={formData.employment_status}
                onChange={(e) => setFormData(prev => ({ ...prev, employment_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={submitting}
              >
                <option value="active">Active</option>
                <option value="on-leave">On Leave</option>
                <option value="suspended">Suspended</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salary</label>
              <input
                type="number"
                value={formData.salary}
                onChange={(e) => setFormData(prev => ({ ...prev, salary: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="0"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
              <select
                value={formData.risk_level}
                onChange={(e) => setFormData(prev => ({ ...prev, risk_level: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={submitting}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Staff Member'}
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4 text-center">
            <span className="text-red-500">*</span> Required fields
          </p>
        </form>
      </motion.div>
    </div>
  );
};

// Enhanced Staff Detail Modal Component (keeping existing implementation)
const StaffDetailModal = ({ staff, cases, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Load timeline data when timeline tab is active
  useEffect(() => {
    if (activeTab === 'timeline' && timelineEvents.length === 0) {
      loadTimelineData();
    }
  }, [activeTab]);

  const loadTimelineData = async () => {
    try {
      setTimelineLoading(true);
      const timeline = await staffService.getStaffTimeline(staff.id);
      setTimelineEvents(timeline);
    } catch (err) {
      console.error('Error loading staff timeline:', err);
    } finally {
      setTimelineLoading(false);
    }
  };

  const stats = {
    totalCases: cases.length,
    activeCases: cases.filter(c => c.status === 'active').length,
    completedCases: cases.filter(c => c.status === 'completed').length,
    highPriorityCases: cases.filter(c => c.priority === 'high').length
  };

  const getRiskLevelColor = level => {
    switch (level) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getCaseStatusColor = status => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'on-hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                {staff.full_name
                  .split(' ')
                  .map(n => n[0])
                  .join('')}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{staff.full_name}</h2>
                <p className="text-gray-600">
                  {staff.position} • {staff.department}
                </p>
                <p className="text-sm text-gray-500">{staff.employee_id}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{stats.totalCases}</p>
              <p className="text-sm text-blue-500">Total Cases</p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">{stats.activeCases}</p>
              <p className="text-sm text-orange-500">Active Cases</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats.completedCases}</p>
              <p className="text-sm text-green-500">Completed</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{stats.highPriorityCases}</p>
              <p className="text-sm text-red-500">High Priority</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-8">
              {[
                { id: 'overview', name: 'Overview', icon: FiUser },
                { id: 'cases', name: 'Cases', icon: FiFileText },
                { id: 'timeline', name: 'Timeline', icon: FiActivity },
                { id: 'performance', name: 'Performance', icon: FiTrendingUp }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <SafeIcon icon={tab.icon} />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiMail} className="text-gray-400" />
                      <span className="text-gray-900">{staff.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiPhone} className="text-gray-400" />
                      <span className="text-gray-900">{staff.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiMapPin} className="text-gray-400" />
                      <span className="text-gray-900">{staff.location}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Employment Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Start Date:</span>
                      <span className="text-gray-900">{new Date(staff.start_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Line Manager:</span>
                      <span className="text-gray-900">{staff.line_manager}</span>
                    </div>
                    {staff.salary && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Salary:</span>
                        <span className="text-gray-900">£{Number(staff.salary).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Risk Level:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getRiskLevelColor(staff.risk_level)}`}>
                        {staff.risk_level.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Performance Overview</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700">Current Rating</span>
                    <span className="text-xl font-bold text-gray-900">{staff.performance_rating}/5.0</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${(staff.performance_rating / 5) * 100}%` }}
                    />
                  </div>
                  {staff.last_review_date && (
                    <p className="text-sm text-gray-600">Last review: {new Date(staff.last_review_date).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'cases' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Employee Cases</h3>
                <span className="text-sm text-gray-500">{cases.length} total cases</span>
              </div>

              {cases.length > 0 ? (
                <div className="space-y-4">
                  {cases.map((case_, index) => (
                    <div key={case_.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">{case_.title}</h4>
                          <p className="text-sm text-gray-600">{case_.case_id} • {case_.case_type}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCaseStatusColor(case_.status)}`}>
                            {case_.status.toUpperCase()}
                          </span>
                          <span className={`text-xs font-medium ${getPriorityColor(case_.priority)}`}>
                            {case_.priority?.toUpperCase()} PRIORITY
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                        <div>
                          <span>Created: {new Date(case_.created_at).toLocaleDateString()}</span>
                        </div>
                        <div>
                          <span>Last Updated: {new Date(case_.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {case_.progress && (
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span>Progress</span>
                            <span>{case_.progress.completionPercentage || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${case_.progress.completionPercentage || 0}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">{case_.description || 'No description available'}</span>
                        <a href={`#/case/${case_.id}`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                          View Details →
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <SafeIcon icon={FiFileText} className="text-4xl mx-auto mb-2 opacity-50" />
                  <p>No cases found for this employee</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Staff Timeline</h3>
                <p className="text-sm text-gray-600">
                  Chronological view of all events across cases involving {staff.full_name}
                </p>
              </div>

              <StaffTimeline events={timelineEvents} loading={timelineLoading} />
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Performance Summary</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Overall Rating</span>
                      <span className="text-xl font-bold text-gray-900">{staff.performance_rating}/5.0</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${(staff.performance_rating / 5) * 100}%` }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {staff.last_review_date && (
                        <>
                          <div>
                            <p className="text-gray-500">Last Review:</p>
                            <p className="font-medium">{new Date(staff.last_review_date).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Next Review Due:</p>
                            <p className="font-medium">
                              {new Date(
                                new Date(staff.last_review_date).setFullYear(
                                  new Date(staff.last_review_date).getFullYear() + 1
                                )
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Edit Staff Modal Component
const EditStaffModal = ({ staffData, onSubmit, onCancel }) => {
  const modalRef = useModalAccessibility(true, onCancel);
  const [formData, setFormData] = useState({
    employee_id: staffData.employee_id || '',  // Include employee_id (required field)
    full_name: staffData.full_name || '',
    email: staffData.email || '',
    phone: staffData.phone || '',
    department: staffData.department || '',
    position: staffData.position || '',
    line_manager: staffData.line_manager || '',
    location: staffData.location || '',
    employment_status: staffData.employment_status || 'active',
    start_date: staffData.start_date || '',  // Include start_date (required field)
    salary: staffData.salary || '',
    risk_level: staffData.risk_level || 'low',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.full_name || !formData.email || !formData.department || !formData.position) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      setError(error.message || 'Failed to update staff member');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Staff Member</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department *
              </label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Department</option>
                <option value="HR">HR</option>
                <option value="IT">IT</option>
                <option value="Finance">Finance</option>
                <option value="Operations">Operations</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position *
              </label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Line Manager
              </label>
              <input
                type="text"
                value={formData.line_manager}
                onChange={(e) => setFormData({ ...formData, line_manager: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Status
              </label>
              <select
                value={formData.employment_status}
                onChange={(e) => setFormData({ ...formData, employment_status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="on-leave">On Leave</option>
                <option value="suspended">Suspended</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Salary
              </label>
              <input
                type="number"
                value={formData.salary}
                onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Risk Level
              </label>
              <select
                value={formData.risk_level}
                onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Updating...' : 'Update Staff'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ title, message, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <SafeIcon icon={FiTrash2} className="text-red-600 text-xl" />
          </div>
          <h2 className="ml-4 text-xl font-bold text-gray-900">{title}</h2>
        </div>

        <p className="text-gray-600 mb-6">{message}</p>

        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default StaffManagement;